<template>
  <v-form ref="form" v-model="formValid" class="mb-16" @submit.prevent="handleFormSubmit">
    <div class="d-flex w-100" style="position: relative">
      <div class="back-btn-container">
        <v-btn
          class="font-weight-bold pa-0"
          style="min-width: 30px"
          icon
          @click="$store.dispatch('actionForm/changeToPreviousPanel')"
        >
          <v-icon>
            {{ icons.mdiArrowLeft }}
          </v-icon>
        </v-btn>
      </div>
    </div>

    <div class="d-flex flex-column align-center px-10">
      <span class="text-h5 text-uppercase">
        {{ $t('action.panels.FUNDRAISER_PAYMENT.heading', locale) }}
      </span>

      <div class="d-flex flex-column align-center w-100">
        <v-btn
          class="font-weight-bold mb-5"
          color="allout-blue"
          text
          small
          @click="$store.dispatch('actionForm/changeToFirstPanel')"
        >
          {{
            $t(
              fundraiserUI.selectedPaymentFrequency === 'one_time'
                ? 'action.panels.FUNDRAISER_PAYMENT.buttons.backToAmount.label.one-time'
                : 'action.panels.FUNDRAISER_PAYMENT.buttons.backToAmount.label.monthly',
              locale,
              { symbol: currentCurrencyConfig.symbol, amount: fundraiserUI.amount }
            )
          }}
        </v-btn>

        <div class="mb-5 w-100">
          <stripe-payment-element
            v-if="stripePaymentElementReady"
            :validation-messages="{
              required: $t('action.fields.payment.validation.required', locale),
              incomplete: $t('action.fields.payment.validation.incomplete', locale)
            }"
            :locale="locale"
            :client-secret="stripeClientSecret"
            :stripe-publishable-key="stripePublishableKey"
            required
            @stripe="handleStripeEmitted"
            @stripe-elements="handleStripeElementsEmitted"
            @change="handleStripePaymentElementChanged"
          />
          <v-skeleton-loader v-else type="image, text, paragraph" />
        </div>

        <div v-if="formGlobalErrors">
          <v-alert
            v-for="error in formGlobalErrors"
            :key="error.code"
            dense
            outlined
            type="error"
            class="mb-3"
          >
            {{ getFormErrorMessage(error) }}
          </v-alert>
        </div>
        <section-data-consent v-if="showOptIn" />
        <transition-group tag="section" name="fade" class="w-100 mt-4">
          <v-btn
            v-if="fundraiserUI.selectedProvider === 'stripe'"
            key="stripe"
            name="donate"
            type="submit"
            color="secondary"
            class="text-uppercase font-weight-bold w-100 mb-4"
            :disabled="!allValid"
            large
            @click="handleDonateButtonClicked"
          >
            {{
              $t(
                fundraiserUI.selectedPaymentFrequency === 'one_time'
                  ? 'action.panels.FUNDRAISER_PAYMENT.buttons.donate.label.one-time'
                  : 'action.panels.FUNDRAISER_PAYMENT.buttons.donate.label.monthly',
                locale,
                { symbol: currentCurrencyConfig.symbol, amount: fundraiserUI.amount }
              )
            }}
          </v-btn>
        </transition-group>
      </div>
    </div>
  </v-form>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { mdiCreditCardOutline, mdiArrowLeft } from '@mdi/js'

import StripePaymentElement from '../ui/StripePaymentElement'
import { consoleError } from 'vuetify/lib/util/console'

export default {
  name: 'PanelFundraiserPayment',
  components: {
    StripePaymentElement
  },
  data: vm => ({
    formValid: false,
    stripe: null,
    stripeElements: null,
    stripePaymentElement: null,
    stripePaymentElementValid: false,
    icons: {
      mdiCreditCardOutline,
      mdiArrowLeft
    }
  }),
  computed: {
    allValid() {
      return this.fundraiserUI.selectedProvider === 'stripe'
        ? this.formValid && this.stripePaymentElementValid
        : this.formValid
    },
    stripePaymentElementReady() {
      return this.fundraiserUI.stripeReady && this.stripeClientSecret
    },
    stripeAccount() {
      return this.currentCurrencyConfig.uk_stripe ? 'acct_1JXotMF6HqNZCaWi' : null
    },
    stripeAccountPublishableKeyUS() {
      return process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY || process.env.stripePublishableKey
    },
    stripeAccountPublishableKeyUK() {
      return process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY_UK || process.env.stripePublishableKeyUK
    },
    stripeAccountPublishableKeyC3() {
      return process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY_C3 || process.env.stripePublishableKeyC3
    },
    stripePublishableKey() {
      if (this.formConfig.is_tax_deductible) {
        return this.stripeAccountPublishableKeyC3
      }
      if (this.currentCurrencyConfig.uk_stripe) {
        return this.stripeAccountPublishableKeyUK
      }
      return this.stripeAccountPublishableKeyUS
    },
    stripeClientSecret() {
      return this.fundraiserUI.stripeClientSecret
    },
    ...mapState('actionForm', [
      'fundraiserUI',
      'locale',
      'formGlobalErrors',
      'formIsDirty',
      'actionID',
      'formConfig'
    ]),
    ...mapGetters('actionForm', ['currentCurrencyConfig', 'showOptIn'])
  },
  methods: {
    handlePaymentTypeChange(value) {
      this.$store.dispatch('actionForm/updatePaymentMethod', {
        paymentTypeIndex: value
      })
    },
    handleDonateButtonClicked() {
      this.$refs.form.validate()
    },
    handleStripeElementsEmitted(stripeElements) {
      this.stripeElements = stripeElements
    },
    handleStripeEmitted(stripe) {
      this.stripe = stripe
    },
    handleStripePaymentElementChanged(event) {
      const { complete, value } = event
      this.stripePaymentElementValid = complete

      // Update the form's postal code field when the stripe element changes
      if (value && value.postalCode) {
        this.$store.commit('actionForm/UPDATE_FORM_DATA_FIELD_VALUES', {
          fieldConfig: {
            name: 'postal_code'
          },
          value: value.postalCode
        })
      }
    },
    handleFormSubmit() {
      const { stripeElements, stripe } = this
      // Process:
      // 1. Donate button clicked
      // 2. Reach out to payment processor via Mega
      // 3. Log success / failure in Mega
      // 4. Mega asynchronously reaches out to AS (with retries)
      if (this.allValid) {
        switch (this.fundraiserUI.selectedProvider) {
          case 'stripe':
            return this.$store.dispatch('actionForm/commitStripePayment', {
              stripe,
              elements: stripeElements
            })
          case 'paypal':
            return this.$store.dispatch('actionForm/commitPayPalPayment')
          default:
            throw new Error(`Unhandled payment provider "${this.fundraiserUI.selectedProvider}"`)
        }
      }
      return null
    },
    getFormErrorMessage(error) {
      const i18nPath = `action.panels.FUNDRAISER_PAYMENT.form.errors.${error.code}`
      // Do we have a translation for this error code?
      if (this.$te(i18nPath, this.locale)) {
        return this.$t(i18nPath, this.locale)
      }
      // Otherwise, return the default error message from the provider
      return error.message
    }
  }
}
</script>

<style lang="scss" scoped>
.back-btn-container {
  position: absolute;
  left: 8px;
  top: 0;
}

::v-deep {
  .StripeElement {
    border-color: rgba(76, 76, 76, 0.41) !important;

    &.StripeElement--focus {
      border-color: rgb(76, 76, 76) !important;
    }
  }

  // properly hide the "hidden" input below the stripe card element so it doesn't add unwanted margins
  button.hide {
    display: none;
  }
}

.paypal-svg {
  width: 20px;
  height: 20px;
  & > svg {
    fill: #04a7cb;
    width: 100%;
    height: 100%;
  }
  &.selected {
    & > svg {
      fill: white;
    }
  }
}

.fade-enter-active {
  transition: opacity 1s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.paypal-wrap {
  &.disabled {
    cursor: not-allowed;
  }
}
</style>
